/**
 * SlideCard - Draggable slide card with config selection from huisstijl
 */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Box,
    Paper,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Chip,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    DragIndicator as DragIcon,
    Delete as DeleteIcon,
    AddPhotoAlternate as AddPhotoIcon,
    Close as CloseIcon,
    PlayCircle as PlayCircleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as WaitingIcon,
} from '@mui/icons-material';

const RENDER_STATUS_ICONS = {
    pending: <WaitingIcon sx={{ fontSize: 16, color: '#9ca3af' }} />,
    rendering: <PlayCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />,
    completed: <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />,
    error: <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />,
};

const CONFIG_STYLES = {
    begin:   { color: '#059669', bg: '#d1fae5', border: '#a7f3d0', label: 'Begin' },
    content: { color: '#3b82f6', bg: '#dbeafe', border: '#93c5fd', label: 'Content' },
    eind:    { color: '#8b5cf6', bg: '#ede9fe', border: '#c4b5fd', label: 'Eind' },
};

function getConfigType(cfg) {
    const type = cfg?.type || '';
    if (type.includes('Begin')) return 'begin';
    if (type.includes('Eind')) return 'eind';
    return 'content';
}

function getConfigLabel(cfg) {
    const type = cfg.type || '';
    if (type === 'Slide_Begin') return 'Begin slide';
    if (type === 'Slide_Eind') return 'Eind slide';
    const m = type.match(/^Slide_(\d+)$/);
    if (m) return `Content slide ${m[1]}`;
    return cfg.label || cfg.compositienaam || type;
}

export default function SlideCard({
    slide,
    slideConfigs = [],
    onUpdate,
    onDelete,
    onUploadFoto,
    onDeleteFoto,
    overlay = false,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: slide.id, disabled: overlay });

    const style = overlay ? {} : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
    };

    // Resolve active config (config_id can be int from DB or pg_123 string)
    const activeConfig = slideConfigs.find(c => c.id === slide.config_id || c.numId === slide.config_id) || null;
    const layers = slide.layers || activeConfig?.velden || ['Titel_1', 'Subtitel_1', 'Foto_1'];
    const titelLayers = layers.filter(l => l.startsWith('Titel_'));
    const subtitelLayers = layers.filter(l => l.startsWith('Subtitel_'));
    const fotoLayers = layers.filter(l => l.startsWith('Foto_'));
    const textValues = slide.text_values || {};

    const handleConfigChange = (configId) => {
        const cfg = slideConfigs.find(c => c.id === configId);
        if (!cfg) return;
        onUpdate(slide.id, {
            config_id: cfg.numId || configId,  // Store numeric ID for DB
            layers: cfg.velden || [],
            slide_type: cfg.type?.includes('Begin') ? 'begin' : cfg.type?.includes('Eind') ? 'eind' : 'content',
        });
    };

    // Color based on slide type
    const slideStyle = CONFIG_STYLES[slide.slide_type] || CONFIG_STYLES.content;

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 6 : 1}
            sx={{
                p: 2,
                border: '2px solid',
                borderColor: isDragging ? '#7c3aed' : slideStyle.border,
                borderRadius: 2,
                bgcolor: isDragging ? '#faf5ff' : '#fff',
                borderTopWidth: 4,
                borderTopColor: slideStyle.color,
                minWidth: 280,
                width: 280,
                flexShrink: 0,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#9ca3af' }}>
                    <DragIcon fontSize="small" />
                </Box>

                {slide.render_status && RENDER_STATUS_ICONS[slide.render_status] && (
                    <Tooltip title={`Render: ${slide.render_status}`}>
                        {RENDER_STATUS_ICONS[slide.render_status]}
                    </Tooltip>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <IconButton size="small" onClick={() => onDelete(slide.id)} sx={{ color: '#ef4444' }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Config selector */}
            {slideConfigs.length > 0 ? (
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel sx={{ fontSize: '0.85rem' }}>Slide configuratie</InputLabel>
                    <Select
                        value={activeConfig?.id || ''}
                        onChange={(e) => handleConfigChange(e.target.value)}
                        label="Slide configuratie"
                        sx={{ fontSize: '0.85rem' }}
                    >
                        {[...slideConfigs].sort((a, b) => {
                            const order = (c) => getConfigType(c) === 'begin' ? 0 : getConfigType(c) === 'eind' ? 2 : 1;
                            if (order(a) !== order(b)) return order(a) - order(b);
                            return (a.type || '').localeCompare(b.type || '');
                        }).map(cfg => {
                            const cfgStyle = CONFIG_STYLES[getConfigType(cfg)] || CONFIG_STYLES.content;
                            return (
                                <MenuItem key={cfg.id} value={cfg.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfgStyle.color, flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{getConfigLabel(cfg)}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>
                                            {cfg.compositienaam}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            ) : (
                <Chip
                    label={slideStyle.label}
                    size="small"
                    sx={{ bgcolor: slideStyle.bg, color: slideStyle.color, fontWeight: 600, fontSize: '0.7rem', height: 22, mb: 1.5 }}
                />
            )}

            {/* Preview image */}
            {slide.preview_url && (
                <Box sx={{ mb: 1.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#000', aspectRatio: '16/9' }}>
                    <img src={slide.preview_url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
            )}

            {/* Foto uploads — one slot per foto layer */}
            {fotoLayers.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {fotoLayers.map((layer, i) => {
                            const url = (slide.foto_urls || [])[i];
                            return (
                                <Box key={layer} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', minWidth: 40 }}>
                                        {layer}
                                    </Typography>
                                    {url ? (
                                        <Box sx={{ position: 'relative', width: 64, height: 48, borderRadius: 1, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                                            <img src={url} alt={layer} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <IconButton size="small" onClick={() => onDeleteFoto(slide.id, i)}
                                                sx={{ position: 'absolute', top: -4, right: -4, bgcolor: 'rgba(255,255,255,0.9)', p: 0.2, '&:hover': { bgcolor: '#fee2e2' } }}>
                                                <CloseIcon sx={{ fontSize: 12 }} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <IconButton size="small" component="label"
                                            sx={{ width: 64, height: 48, borderRadius: 1, border: '1px dashed #d1d5db', color: '#9ca3af', '&:hover': { borderColor: '#7c3aed', color: '#7c3aed' } }}>
                                            <AddPhotoIcon fontSize="small" />
                                            <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                                                onChange={(e) => { if (e.target.files[0]) onUploadFoto(slide.id, e.target.files[0]); e.target.value = ''; }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Dynamic text fields */}
            {titelLayers.map(l => (
                <TextField key={l} label={l.replace('_', ' ')} size="small" fullWidth
                    value={textValues[l] || ''}
                    onChange={(e) => onUpdate(slide.id, { text_values: { ...textValues, [l]: e.target.value } })}
                    sx={{ mb: 0.75 }} InputProps={{ sx: { fontSize: '0.85rem' } }} />
            ))}
            {subtitelLayers.map(l => (
                <TextField key={l} label={l.replace('_', ' ')} size="small" fullWidth
                    value={textValues[l] || ''}
                    onChange={(e) => onUpdate(slide.id, { text_values: { ...textValues, [l]: e.target.value } })}
                    sx={{ mb: 0.75 }} InputProps={{ sx: { fontSize: '0.85rem' } }} />
            ))}

            {/* Duration */}
            <Box sx={{ px: 0.5, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Duur: {slide.duration || 6}s
                </Typography>
                <Slider value={slide.duration || 6} onChange={(e, val) => onUpdate(slide.id, { duration: val })}
                    min={2} max={30} step={0.5} size="small" sx={{ py: 0.5 }} />
            </Box>
        </Paper>
    );
}
